import React, { useState, useEffect } from 'react';
import { Clock, MapPin, ChevronLeft, ChevronRight, Plus, RefreshCw, Loader2 } from 'lucide-react';

// Always use local timezone for date strings
const formatDateLocal = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Format hours/minutes to 12-hour clock (e.g. "2:30 PM")
const formatTime12h = (hours, minutes) => {
  const period = hours >= 12 ? 'PM' : 'AM';
  const h = hours % 12 || 12;
  const m = String(minutes).padStart(2, '0');
  return `${h}:${m} ${period}`;
};

const WeeklyCalendarView = ({ 
  userId, 
  localEvents = [], 
  nylasEvents = [], 
  outlookEvents = [],
  nylasAccounts = [],
  onEventClick,
  onAddEvent,
  onEventDrop
}) => {
  // Initialize to current week (starting from the Sunday of the current week)
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek);
    return startOfWeek;
  });

  // Drag-and-drop state
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);

  const handlePrevWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const getWeekDates = () => {
    const dates = [];
    const startOfWeek = new Date(currentWeekStart);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const formatWeekRange = () => {
    const weekDates = getWeekDates();
    const startDate = weekDates[0];
    const endDate = weekDates[6];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[startDate.getMonth()]} ${startDate.getDate()} - ${months[endDate.getMonth()]} ${endDate.getDate()}, ${startDate.getFullYear()}`;
  };

  // Get events for a specific day combining local and Nylas events
  const getEventsForDay = (dayIndex) => {
    const weekDates = getWeekDates();
    const targetDate = weekDates[dayIndex];
    const targetDateStr = formatDateLocal(targetDate);
    const targetDayOfWeek = targetDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    const events = [];
    
    // Add local events
    localEvents.forEach(event => {
      // Derive date from start_time (like the month view does) to avoid timezone issues
      let eventDateStr;
      let eventDayOfWeek = null;
      if (event.start_time) {
        const d = new Date(event.start_time);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        eventDateStr = `${y}-${m}-${dd}`;
        eventDayOfWeek = d.getDay();
      } else if (event.date) {
        eventDateStr = event.date;
        eventDayOfWeek = new Date(event.date).getDay();
      } else if (event.year !== undefined && event.month !== undefined && event.day !== undefined) {
        eventDateStr = `${event.year}-${String(event.month + 1).padStart(2, '0')}-${String(event.day).padStart(2, '0')}`;
        eventDayOfWeek = new Date(event.year, event.month, event.day).getDay();
      }
      
      // Check if event matches this day:
      // 1. Exact date match, OR
      // 2. Weekly recurring event on the same day of week
      const isExactMatch = eventDateStr === targetDateStr;
      const isWeeklyRecurring = event.recurrence === 'weekly' && eventDayOfWeek === targetDayOfWeek;
      
      if (isExactMatch || isWeeklyRecurring) {
        let hours = 9, minutes = 0;
        if (event.start_time) {
          const d = new Date(event.start_time);
          hours = d.getHours();
          minutes = d.getMinutes();
        } else if (event.startTime) {
          [hours, minutes] = event.startTime.split(':').map(Number);
        }
        
        let duration = 1;
        if (event.start_time && event.end_time) {
          duration = (new Date(event.end_time) - new Date(event.start_time)) / 3600000;
        } else if (event.duration) {
          duration = event.duration;
        }
        
        const timeStr = formatTime12h(hours, minutes);
        
        events.push({
          ...event,
          hour: hours,
          minute: minutes || 0,
          duration: Math.min(duration, 4) || 1,
          title: event.title,
          time: timeStr,
          color: event.color,
          category: event.category || event.type || 'other',
          location: event.location || '',
          source: 'local',
          isRecurring: isWeeklyRecurring && !isExactMatch
        });
      }
    });
    
    // Add Nylas events
    nylasEvents.forEach(event => {
      const eventDate = event.when?.start_date || (event.when?.start_time ? new Date(event.when.start_time * 1000).toISOString().split('T')[0] : null);
      if (eventDate === targetDateStr) {
        let hours = 9, minutes = 0;
        if (event.when?.start_time) {
          const startDate = new Date(event.when.start_time * 1000);
          hours = startDate.getHours();
          minutes = startDate.getMinutes();
        }
        
        let duration = 1;
        if (event.when?.start_time && event.when?.end_time) {
          duration = (event.when.end_time - event.when.start_time) / 3600;
        }
        
        events.push({
          ...event,
          hour: hours,
          minute: minutes,
          duration: Math.min(duration, 4),
          title: event.title,
          time: formatTime12h(hours, minutes),
          color: 'purple',
          location: event.location || '',
          source: 'nylas',
          accountEmail: event.account_email
        });
      }
    });

    // Add Outlook / Calendar.app synced events
    outlookEvents.forEach(event => {
      // Derive the local Y-M-D from the event's ISO start
      const startIso = event.start_time || event.start;
      if (!startIso) return;
      const d = new Date(startIso);
      if (isNaN(d.getTime())) return;
      const eventDateStr = formatDateLocal(d);
      if (eventDateStr !== targetDateStr) return;

      const isAllDay = !!event.all_day || !!event.is_all_day;
      const hours = isAllDay ? 0 : d.getHours();
      const minutes = isAllDay ? 0 : d.getMinutes();

      // Duration in hours; fall back to 1 if end missing
      let duration = 1;
      const endIso = event.end_time || event.end;
      if (endIso) {
        const e = new Date(endIso);
        if (!isNaN(e.getTime())) {
          duration = Math.max(0.25, (e.getTime() - d.getTime()) / 3600000);
        }
      }

      events.push({
        ...event,
        hour: hours,
        minute: minutes,
        duration: Math.min(duration, 4),
        title: event.title || event.subject || event.name || 'Event',
        time: isAllDay ? 'All Day' : formatTime12h(hours, minutes),
        color: event.color,
        category: event.category || event.type || 'other',
        location: event.location || '',
        source: 'outlook',
      });
    });
    
    return events.sort((a, b) => (a.hour * 60 + a.minute) - (b.hour * 60 + b.minute));
  };

  const hours = Array.from({ length: 24 }, (_, i) => (i + 6) % 24); // 6 AM to 5 AM next day (24 hours)
  // Create time slots with both :00 and :30 for each hour
  const timeSlots = hours.flatMap(hour => [
    { hour, minute: 0 },
    { hour, minute: 30 }
  ]);
  const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const weekDates = getWeekDates();
  const today = new Date();
  const todayStr = formatDateLocal(today);

  const getEventColor = (event) => {
    // Category-based color mapping
    const categoryColors = {
      'meeting': 'blue',
      'work': 'blue',
      'cresa': 'emerald',
      'birthday': 'pink',
      'health': 'red',
      'sports': 'orange',
      'religious': 'purple',
      'travel': 'cyan',
      'home': 'green',
      'social': 'yellow',
      'education': 'indigo',
      'networking': 'teal',
      'other': 'gray'
    };
    
    // Use event.color if set, otherwise derive from category
    const color = event.color || categoryColors[event.category] || categoryColors[event.type] || 'green';
    
    const colors = {
      blue: 'bg-blue-600/90 border-blue-400',
      purple: 'bg-purple-600/90 border-purple-400',
      green: 'bg-green-600/90 border-green-400',
      orange: 'bg-orange-500/90 border-orange-400',
      pink: 'bg-pink-500/90 border-pink-400',
      indigo: 'bg-indigo-600/90 border-indigo-400',
      teal: 'bg-teal-500/90 border-teal-400',
      red: 'bg-red-600/90 border-red-400',
      yellow: 'bg-yellow-500/90 border-yellow-400',
      cyan: 'bg-cyan-500/90 border-cyan-400',
      emerald: 'bg-emerald-500/90 border-emerald-400',
      gray: 'bg-gray-600/90 border-gray-400',
    };
    return colors[color] || colors.green;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Week Navigation Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <button
          onClick={handlePrevWeek}
          className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        
        <h2 className="text-lg font-semibold text-white">
          {formatWeekRange()}
        </h2>
        
        <button
          onClick={handleNextWeek}
          className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Week Grid */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-[800px]">
          {/* Day Headers */}
          <div className="grid grid-cols-8 border-b border-gray-800 sticky top-0 bg-black z-10">
            <div className="p-2 text-xs text-gray-500 border-r border-gray-800"></div>
            {weekDates.map((date, i) => {
              const dateStr = formatDateLocal(date);
              const isToday = dateStr === todayStr;
              return (
                <div 
                  key={i} 
                  className={`p-2 text-center border-r border-gray-800 ${isToday ? 'bg-purple-900/30' : ''}`}
                >
                  <div className="text-xs text-gray-400">{dayNames[i]}</div>
                  <div className={`text-lg font-bold ${isToday ? 'text-purple-400' : 'text-white'}`}>
                    {date.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time Slot Rows (every 30 minutes) */}
          {timeSlots.map((slot, slotIndex) => {
            const { hour, minute } = slot;
            const isHalfHour = minute === 30;
            return (
            <div key={`${hour}-${minute}`} className={`grid grid-cols-8 ${isHalfHour ? 'border-b border-gray-800/30' : 'border-b border-gray-800/50'}`}>
              {/* Time Label */}
              <div className={`p-2 text-xs text-gray-500 border-r border-gray-800 text-right pr-3 ${isHalfHour ? 'text-gray-600' : ''}`}>
                {formatTime12h(hour, minute)}
              </div>
              
              {/* Day Cells */}
              {weekDates.map((date, dayIndex) => {
                const dateStr = formatDateLocal(date);
                const isToday = dateStr === todayStr;
                // Filter events that start in this 30-min slot (e.g., 9:00-9:29 or 9:30-9:59)
                const dayEvents = getEventsForDay(dayIndex).filter(e => 
                  e.hour === hour && e.minute >= minute && e.minute < minute + 30
                );
                const slotKey = `${dayIndex}-${hour}-${minute}`;
                const isDropTarget = dragOverSlot === slotKey;
                
                return (
                  <div 
                    key={dayIndex} 
                    className={`relative min-h-[70px] border-r border-gray-800/50 transition-colors ${isToday ? 'bg-purple-900/10' : ''} ${isDropTarget ? 'bg-blue-900/30 ring-1 ring-blue-400/50' : ''}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      setDragOverSlot(slotKey);
                    }}
                    onDragLeave={() => setDragOverSlot(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOverSlot(null);
                      if (draggedEvent && onEventDrop) {
                        // Pass the exact time slot (hour and minute) for more precise dropping
                        onEventDrop(draggedEvent, date, hour, minute);
                      }
                      setDraggedEvent(null);
                    }}
                  >
                    {dayEvents.map((event, eventIndex) => (
                      <div
                        key={eventIndex}
                        draggable={event.source === 'local'}
                        onDragStart={(e) => {
                          if (event.source !== 'local') { e.preventDefault(); return; }
                          setDraggedEvent(event);
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', event.id);
                          e.target.style.opacity = '0.5';
                        }}
                        onDragEnd={(e) => {
                          e.target.style.opacity = '1';
                          setDraggedEvent(null);
                          setDragOverSlot(null);
                        }}
                        onClick={() => {
                          const eventDay = date.getDate();
                          onEventClick?.(event, eventDay);
                        }}
                        className={`absolute left-1 right-1 rounded-lg p-2 cursor-pointer border-l-2 ${getEventColor(event)} hover:opacity-90 transition-opacity overflow-hidden ${event.source === 'local' ? 'cursor-grab active:cursor-grabbing' : ''}`}
                        style={{
                          top: `${((event.minute - minute) / 30) * 100}%`,
                          height: `${Math.max(event.duration * 140, 60)}px`,
                          zIndex: eventIndex + 1
                        }}
                      >
                        <div className="text-sm font-medium text-white truncate">{event.title}</div>
                        <div className="text-xs text-white/70 flex items-center gap-1 mt-0.5">
                          <Clock size={12} />
                          {event.time}
                        </div>
                        {event.location && (
                          <div className="text-xs text-white/70 flex items-center gap-1 truncate mt-0.5">
                            <MapPin size={12} />
                            {event.location}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )})}
        </div>
      </div>
    </div>
  );
};

export default WeeklyCalendarView;
